import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Grid,
  Avatar,
  Divider,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Pagination,
  Stack,
  Paper,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ExpandMore,
  CheckCircle,
  Cancel,
  Timer,
  Grade,
  Visibility,
  Refresh,
  Assessment,
  TrendingUp,
  Speed,
  School,
  DateRange,
  FilterList,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { adaptiveTestingService } from '../services/adaptiveTestingService';

const StyledCard = styled(Card)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const TestSessionCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 12px;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
`;

const AnswerCard = styled(Card)`
  margin-bottom: 16px;
  border-left: 4px solid;
  border-left-color: ${props => props.theme.palette.grey[300]};
  
  &.correct {
    border-left-color: ${props => props.theme.palette.success.main};
  }
  
  &.incorrect {
    border-left-color: ${props => props.theme.palette.error.main};
  }
`;

const TestHistoryComponent: React.FC = () => {
  const { student } = useAuth();
  const theme = useTheme();
  const [testHistory, setTestHistory] = useState<any[]>([]);
  const [detailedSessions, setDetailedSessions] = useState<{[key: string]: any}>({});
  const [answerHistory, setAnswerHistory] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSession, setExpandedSession] = useState<string | null>(null);
  const [subjectFilter, setSubjectFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState<any>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [responsePagination, setResponsePagination] = useState<any>(null);

  useEffect(() => {
    if (student?._id) {
      fetchTestHistory();
      fetchAnswerHistory();
    }
  }, [student?._id, currentPage, pageSize, subjectFilter, startDate, endDate]);

  const fetchTestHistory = async () => {
    if (!student?._id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Fetching test history for student:', student._id);
      const data = await adaptiveTestingService.getStudentHistory(student._id, {
        page: currentPage,
        limit: pageSize,
        subject: subjectFilter === 'all' ? undefined : subjectFilter,
        startDate: startDate || undefined,
        endDate: endDate || undefined
      });
      console.log('✅ Test history fetched successfully:', data);
      setTestHistory(data.testHistory || []);
      setPagination(data.pagination);
    } catch (err: any) {
      console.error('❌ Error fetching test history:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnswerHistory = async () => {
    if (!student?._id) return;
    
    try {
      console.log('🔍 Fetching answer history for student:', student._id);
      const data = await adaptiveTestingService.getStudentResponses(student._id, {
        page: 1,
        limit: 100,
        subject: subjectFilter === 'all' ? undefined : subjectFilter,
        startDate: startDate || undefined,
        endDate: endDate || undefined
      });
      console.log('✅ Answer history fetched successfully:', data);
      setAnswerHistory(data);
      setResponsePagination(data.pagination);
    } catch (err: any) {
      console.error('❌ Error fetching answer history:', err);
    }
  };

  const fetchDetailedSession = async (sessionId: string) => {
    try {
      if (detailedSessions[sessionId]) {
        return; // Already loaded
      }
      
      const data = await adaptiveTestingService.getDetailedSession(sessionId);
      setDetailedSessions(prev => ({
        ...prev,
        [sessionId]: data
      }));
    } catch (err: any) {
      console.error('Error fetching detailed session:', err);
    }
  };

  const handleSessionExpand = (sessionId: string) => {
    if (expandedSession === sessionId) {
      setExpandedSession(null);
    } else {
      setExpandedSession(sessionId);
      fetchDetailedSession(sessionId);
    }
  };

  const handleSubjectFilterChange = (event: any) => {
    setSubjectFilter(event.target.value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(event.target.value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleClearFilters = () => {
    setSubjectFilter('all');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  // Filter test sessions based on selected subject (client-side filter for display)
  const filteredTestHistory = subjectFilter === 'all' 
    ? testHistory 
    : testHistory.filter(session => session.subject === subjectFilter);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getSubjectColor = (subject: string) => {
    const colors: {[key: string]: string} = {
      math: '#2196F3',
      science: '#4CAF50',
      english: '#FF9800',
      gk: '#9C27B0',
    };
    return colors[subject] || '#757575';
  };

  const MetricCard = ({ title, value, icon, color, subtitle }: any) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Card
        sx={{
          background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
          border: `1px solid ${alpha(color, 0.1)}`,
          transition: 'all 0.3s ease',
          height: '100%',
          minHeight: 120,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 20px ${alpha(color, 0.15)}`,
          },
        }}
      >
        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', height: '100%' }}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color, mb: 1 }}>
                  {value}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  {title}
                </Typography>
                {subtitle && (
                  <Typography variant="body2" color="textSecondary">
                    {subtitle}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: alpha(color, 0.1),
                color,
              }}
            >
              {icon}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );

  if (loading) {
    return (
      <StyledCard>
        <CardContent sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading test history...
          </Typography>
        </CardContent>
      </StyledCard>
    );
  }

  if (error) {
    return (
      <StyledCard>
        <CardContent>
          <Alert severity="error">
            {error}
          </Alert>
        </CardContent>
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      <CardContent sx={{ px: { xs: 2, sm: 3 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6" fontWeight={600}>
            Test History & Performance Analytics
          </Typography>
          <IconButton onClick={() => { fetchTestHistory(); fetchAnswerHistory(); }}>
            <Refresh />
          </IconButton>
        </Box>

        {/* Filters */}
        <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <FilterList sx={{ color: 'primary.main' }} />
            <Typography variant="subtitle1" fontWeight={600}>
              Filters
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Subject</InputLabel>
              <Select
                value={subjectFilter}
                label="Subject"
                onChange={handleSubjectFilterChange}
              >
                <MenuItem value="all">All Subjects</MenuItem>
                <MenuItem value="math">Math</MenuItem>
                <MenuItem value="science">Science</MenuItem>
                <MenuItem value="english">English</MenuItem>
                <MenuItem value="gk">General Knowledge</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={startDate}
              onChange={handleStartDateChange}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
            
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={endDate}
              onChange={handleEndDateChange}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
            
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFilters}
              sx={{ minWidth: 120 }}
            >
              Clear Filters
            </Button>
          </Stack>
        </Paper>

        {/* Analytics Cards */}
        {answerHistory && answerHistory.analytics ? (
          <Box sx={{ mb: 4 }}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Typography variant="subtitle1" fontWeight={600}>
                Performance Overview
              </Typography>
              {subjectFilter !== 'all' && (
                <Chip 
                  label={`${subjectFilter.toUpperCase()} Only`} 
                  size="small" 
                  color="primary"
                  sx={{ bgcolor: getSubjectColor(subjectFilter), color: 'white' }}
                />
              )}
            </Box>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, 
              gap: 2, 
              mb: 3 
            }}>
              <MetricCard
                title="Total Questions"
                value={answerHistory.analytics.totalAnswers}
                icon={<Assessment />}
                color={theme.palette.primary.main}
                subtitle="Questions attempted"
              />
              <MetricCard
                title="Accuracy Rate"
                value={`${answerHistory.analytics.accuracyRate}%`}
                icon={<TrendingUp />}
                color={theme.palette.success.main}
                subtitle="Overall accuracy"
              />
              <MetricCard
                title="Avg. Time"
                value={`${Math.round(answerHistory.analytics.averageTime)}s`}
                icon={<Timer />}
                color={theme.palette.warning.main}
                subtitle="Per question"
              />
              <MetricCard
                title="Fastest Answer"
                value={`${answerHistory.analytics.fastestAnswer}s`}
                icon={<Speed />}
                color={theme.palette.info.main}
                subtitle="Personal best"
              />
            </Box>
          </Box>
        ) : null}

        {/* Test Sessions */}
        <Box>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Typography variant="subtitle1" fontWeight={600}>
              Test Sessions
            </Typography>
            {subjectFilter !== 'all' && (
              <Chip 
                label={`${subjectFilter.toUpperCase()} Only`} 
                size="small" 
                color="primary"
                sx={{ bgcolor: getSubjectColor(subjectFilter), color: 'white' }}
              />
            )}
            <Typography variant="body2" color="text.secondary">
              ({pagination ? pagination.total : filteredTestHistory.length} {(pagination ? pagination.total : filteredTestHistory.length) === 1 ? 'session' : 'sessions'})
            </Typography>
          </Box>
          {filteredTestHistory.length === 0 ? (
            <Box textAlign="center" py={3}>
              <School sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="subtitle1" color="text.secondary">
                {subjectFilter === 'all' ? 'No completed tests yet' : `No ${subjectFilter.toUpperCase()} tests completed yet`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {subjectFilter === 'all' 
                  ? 'Take your first test to see detailed history here!' 
                  : `Take a ${subjectFilter.toUpperCase()} test to see it here!`}
              </Typography>
            </Box>
          ) : (
            <Box>
              {filteredTestHistory.map((session) => (
                <TestSessionCard key={session.sessionId}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar 
                          sx={{ 
                            bgcolor: getSubjectColor(session.subject),
                            width: 40,
                            height: 40,
                          }}
                        >
                          {session.subject.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {session.subject.toUpperCase()} Test
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(session.completedAt)}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box display="flex" alignItems="center" gap={2}>
                        <Box textAlign="center">
                          <Typography variant="subtitle1" fontWeight={600}>
                            {session.score}%
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Score
                          </Typography>
                        </Box>
                        <Box textAlign="center">
                          <Typography variant="subtitle1" fontWeight={600}>
                            {session.accuracyRate}%
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Accuracy
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Visibility />}
                          onClick={() => handleSessionExpand(session.sessionId)}
                        >
                          {expandedSession === session.sessionId ? 'Hide' : 'Review'}
                        </Button>
                      </Box>
                    </Box>

                    {expandedSession === session.sessionId && (
                      <Box mt={3}>
                        <Divider sx={{ mb: 2 }} />
                        {detailedSessions[session.sessionId] ? (
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                              Questions & Answers
                            </Typography>
                            {detailedSessions[session.sessionId].responses.map((response: any) => (
                              <AnswerCard 
                                key={response.questionOrder}
                                className={response.isCorrect ? 'correct' : 'incorrect'}
                              >
                                <CardContent sx={{ py: 2 }}>
                                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                    <Box flex={1}>
                                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                                        <Typography variant="body2" color="text.secondary">
                                          Q{response.questionOrder}
                                        </Typography>
                                        <Chip 
                                          label={response.question.topic} 
                                          size="small" 
                                          variant="outlined"
                                        />
                                        <Chip 
                                          label={response.timeTakenFormatted} 
                                          size="small" 
                                          icon={<Timer />}
                                          variant="outlined"
                                        />
                                      </Box>
                                      <Typography variant="body1" fontWeight={500} mb={1}>
                                        {response.question.text}
                                      </Typography>
                                      <Box display="flex" gap={2} mb={1} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                                        <Box>
                                          <Typography variant="body2" color="text.secondary">
                                            Your Answer:{' '}
                                            <Box component="span" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                              {response.studentAnswer}
                                            </Box>
                                          </Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="body2" color="text.secondary">
                                            Correct Answer:{' '}
                                            <Box component="span" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                              {(() => {
                                                const { correctAnswer, questionSnapshot } = response;
                                                if (!questionSnapshot || !questionSnapshot.options) {
                                                  return correctAnswer; // Fallback
                                                }

                                                // If correctAnswer is 'A', 'B', etc., find the full text
                                                if (correctAnswer.length === 1 && /[A-D]/.test(correctAnswer)) {
                                                  const optionIndex = correctAnswer.charCodeAt(0) - 'A'.charCodeAt(0);
                                                  if (optionIndex >= 0 && optionIndex < questionSnapshot.options.length) {
                                                    const optionText = questionSnapshot.options[optionIndex];
                                                    if (optionText.trim().startsWith(correctAnswer + '.')) {
                                                      return optionText;
                                                    }
                                                    return `${correctAnswer}. ${optionText}`;
                                                  }
                                                }

                                                return correctAnswer; // Return as is if it's already full text
                                              })()}
                                            </Box>
                                          </Typography>
                                        </Box>
                                      </Box>
                                      {response.question.explanation && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                          <strong>Explanation:</strong> {response.question.explanation}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Box textAlign="center" ml={2}>
                                      {response.isCorrect ? (
                                        <CheckCircle color="success" sx={{ fontSize: 28 }} />
                                      ) : (
                                        <Cancel color="error" sx={{ fontSize: 28 }} />
                                      )}
                                    </Box>
                                  </Box>
                                </CardContent>
                              </AnswerCard>
                            ))}
                          </Box>
                        ) : (
                          <Box textAlign="center" py={2}>
                            <CircularProgress size={24} />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Loading detailed results...
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </TestSessionCard>
              ))}
            </Box>
          )}
          
          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <Box display="flex" justifyContent="center" alignItems="center" mt={3}>
              <Stack spacing={2} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total} results
                </Typography>
                <Pagination
                  count={pagination.pages}
                  page={pagination.page}
                  onChange={handlePageChange}
                  color="primary"
                  size="medium"
                  showFirstButton
                  showLastButton
                />
              </Stack>
            </Box>
          )}
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default TestHistoryComponent; 